import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { ulid } from 'ulid';

export type OrderStatus = '등록됨' | '진행중' | '반려' | '완료';
export type PaymentStatus = '등록됨' | '진행중' | '완료' | '취소';
export type OrderTag = '블랙' | '원스텝' | '특수작업';
export type JobType = '캡션' | '번역';
export type JobScope = '화면' | '음성' | '화면+음성';

export interface Customer {
  name: string;
  email: string;
}

export interface JamakeJob {
  id: string;
  name: string;
  registeredAt: string;
  sourceLanguage: string;
  targetLanguage: string;
  jobType: JobType;
  jobScope: JobScope;
  requirements?: string;
  glossary?: string;
  videoDuration: string;
}

export interface Order {
  id: string;
  tags: OrderTag[];
  customer: Customer;
  registeredAt: string;
  title: string;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  totalAmount: number;
  paidAmount: number;
  isSubscription: boolean;
  jobs: JamakeJob[];
}

export interface OrderFilters {
  search: string;
  dateRange: {
    start: string;
    end: string;
  };
  statuses: OrderStatus[];
  tags: OrderTag[];
}

export interface OrderStore {
  // Data
  orders: Order[];
  filters: OrderFilters;
  currentPage: number;
  pageSize: number;
  totalCount: number;
  
  // Computed
  filteredOrders: Order[];
  totalPages: number;
  
  // Actions
  setFilters: (filters: Partial<OrderFilters>) => void;
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  resetFilters: () => void;
  getOrdersByIds: (orderIds: string[]) => Order[];
  
  // Internal methods
  updateFilteredOrders: () => void;
}

// JamakeJob 생성 헬퍼 함수
const createJob = (
  name: string,
  sourceLanguage: string,
  targetLanguage: string,
  jobType: JobType,
  jobScope: JobScope,
  videoDuration: string,
  registeredAt: string,
  requirements?: string,
  glossary?: string
): JamakeJob => ({
  id: ulid(),
  name,
  registeredAt,
  sourceLanguage,
  targetLanguage,
  jobType,
  jobScope,
  requirements,
  glossary,
  videoDuration
});

// 기본 샘플 데이터 (jobs 없이)
const baseSampleOrders: Omit<Order, 'jobs'>[] = [
  {
    id: '01JGHPS5K2ELKFRQ8XVTXSYQ2V',
    tags: ['블랙', '특수작업'],
    customer: { name: '김지민', email: 'jimin.kim@hybe.com' },
    registeredAt: '2024/03/15',
    title: 'BTS (방탄소년단) – Dynamite 리믹스 버전 (MIDI Remake)',
    status: '진행중',
    paymentStatus: '완료',
    totalAmount: 500000,
    paidAmount: 450000,
    isSubscription: false,
  },
  {
    id: '01JGHPS6M4FMKFRN2YVTXSYQ3W',
    tags: ['원스텝'],
    customer: { name: '김채원', email: 'chaewon.kim@hybe.com' },
    registeredAt: '2024/03/14',
    title: 'LE SSERAFIM (르세라핌) – UNFORGIVEN 커버 버전 제작',
    status: '완료',
    paymentStatus: '완료',
    totalAmount: 300000,
    paidAmount: 300000,
    isSubscription: true,
  },
  {
    id: '01JGHPS7P6GNKFRP4ZVTXSYQ4X',
    tags: ['블랙'],
    customer: { name: '안유진', email: 'yujin.ahn@starship-ent.com' },
    registeredAt: '2024/03/13',
    title: 'IVE (아이브) – LOVE DIVE 어쿠스틱 버전 (MIDI Remake)',
    status: '등록됨',
    paymentStatus: '등록됨',
    totalAmount: 250000,
    paidAmount: 50000,
    isSubscription: false,
  },
  {
    id: '01JGHPS8R8HPKFRQ6BVTXSYQ5Y',
    tags: ['특수작업', '원스텝'],
    customer: { name: '전정국', email: 'jungkook.jeon@hybe.com' },
    registeredAt: '2024/03/12',
    title: 'BTS (방탄소년단) – Butter 오케스트라 편곡 버전',
    status: '반려',
    paymentStatus: '취소',
    totalAmount: 800000,
    paidAmount: 0,
    isSubscription: false,
  },
  {
    id: '01JGHPS9T2JQKFRN8CVTXSYQ6Z',
    tags: ['블랙'],
    customer: { name: '사쿠라', email: 'sakura.miyawaki@hybe.com' },
    registeredAt: '2024/03/11',
    title: 'LE SSERAFIM (르세라핌) – ANTIFRAGILE 피아노 솔로 버전',
    status: '완료',
    paymentStatus: '완료',
    totalAmount: 180000,
    paidAmount: 180000,
    isSubscription: true,
  },
  {
    id: '01JGHPSB4KRKFRP2DVTXSYQ7A',
    tags: ['원스텝'],
    customer: { name: '장원영', email: 'wonyoung.jang@starship-ent.com' },
    registeredAt: '2024/03/10',
    title: 'IVE (아이브) – After LIKE 일렉트로닉 리믹스 (MIDI Remake)',
    status: '진행중',
    paymentStatus: '진행중',
    totalAmount: 350000,
    paidAmount: 175000,
    isSubscription: false,
  },
  {
    id: '01JGHPSC6MSLFR4EVTXSYQ8B',
    tags: ['특수작업'],
    customer: { name: '김태형', email: 'taehyung.kim@hybe.com' },
    registeredAt: '2024/03/09',
    title: 'BTS (방탄소년단) – Spring Day 재즈 편곡 버전',
    status: '완료',
    paymentStatus: '완료',
    totalAmount: 600000,
    paidAmount: 600000,
    isSubscription: false,
  },
  {
    id: '01JGHPSD8PTNKFRQ6FVTXSYQ9C',
    tags: ['블랙', '원스텝'],
    customer: { name: '가을', email: 'gaeul.kim@starship-ent.com' },
    registeredAt: '2024/03/08',
    title: 'IVE (아이브) – ELEVEN 하우스 리믹스 (MIDI Remake)',
    status: '진행중',
    paymentStatus: '진행중',
    totalAmount: 420000,
    paidAmount: 210000,
    isSubscription: true,
  },
  {
    id: '01JGHPSEBQUPKFRN8GVTXSYQ1D',
    tags: ['특수작업'],
    customer: { name: '허윤진', email: 'yunjin.huh@hybe.com' },
    registeredAt: '2024/03/07',
    title: 'LE SSERAFIM (르세라핌) – FEARLESS 록 밴드 버전',
    status: '등록됨',
    paymentStatus: '등록됨',
    totalAmount: 450000,
    paidAmount: 90000,
    isSubscription: false,
  },
  {
    id: '01JGHPSFCRVQKFRP2HVTXSYQ2E',
    tags: ['블랙'],
    customer: { name: '박지민', email: 'jimin.park@hybe.com' },
    registeredAt: '2024/03/06',
    title: 'BTS (방탄소년단) – Boy With Luv 어쿠스틱 기타 버전',
    status: '완료',
    paymentStatus: '완료',
    totalAmount: 280000,
    paidAmount: 280000,
    isSubscription: false,
  },
  {
    id: '01JGHPSGDTWRKFRQ4JVTXSYQ3F',
    tags: ['원스텝', '블랙'],
    customer: { name: '카리나', email: 'karina@sm-ent.com' },
    registeredAt: '2024/03/05',
    title: 'aespa (에스파) – Next Level 보사노바 버전',
    status: '진행중',
    paymentStatus: '완료',
    totalAmount: 380000,
    paidAmount: 380000,
    isSubscription: true,
  },
  {
    id: '01JGHPSHFVXSKFRN6KVTXSYQ4G',
    tags: ['특수작업'],
    customer: { name: '윈터', email: 'winter@sm-ent.com' },
    registeredAt: '2024/03/04',
    title: 'aespa (에스파) – Savage 어쿠스틱 기타 버전',
    status: '반려',
    paymentStatus: '취소',
    totalAmount: 320000,
    paidAmount: 0,
    isSubscription: false,
  },
  {
    id: '01JGHPSJHXYTLFR8LVTXSYQ5H',
    tags: ['원스텝'],
    customer: { name: '지젤', email: 'giselle@sm-ent.com' },
    registeredAt: '2024/03/03',
    title: 'aespa (에스파) – Girls 트랩 리믹스 버전',
    status: '등록됨',
    paymentStatus: '등록됨',
    totalAmount: 400000,
    paidAmount: 200000,
    isSubscription: false,
  },
  {
    id: '01JGHPSKJZAUKFRQ2MVTXSYQ6J',
    tags: ['블랙'],
    customer: { name: '닝닝', email: 'ningning@sm-ent.com' },
    registeredAt: '2024/03/02',
    title: 'aespa (에스파) – Black Mamba 심포닉 메탈 버전',
    status: '완료',
    paymentStatus: '완료',
    totalAmount: 520000,
    paidAmount: 520000,
    isSubscription: true,
  },
  {
    id: '01JGHPSLM1BVKFRN4NVTXSYQ7K',
    tags: ['특수작업', '원스텝'],
    customer: { name: '민윤기', email: 'yoongi.min@hybe.com' },
    registeredAt: '2024/03/01',
    title: 'BTS SUGA – Daechwita 클래식 오케스트라 편곡',
    status: '진행중',
    paymentStatus: '진행중',
    totalAmount: 750000,
    paidAmount: 300000,
    isSubscription: false,
  },
  {
    id: 'ORD-016',
    tags: ['블랙'],
    customer: { name: '정호석', email: 'hoseok.jung@hybe.com' },
    registeredAt: '2024/02/29',
    title: 'BTS J-Hope – More 라틴 리듬 편곡',
    status: '완료',
    paymentStatus: '완료',
    totalAmount: 480000,
    paidAmount: 480000,
    isSubscription: false,
  },
  {
    id: 'ORD-017',
    tags: ['원스텝'],
    customer: { name: '김진', email: 'jin.kim@hybe.com' },
    registeredAt: '2024/02/28',
    title: 'BTS 진 – The Astronaut 어쿠스틱 발라드 버전',
    status: '진행중',
    paymentStatus: '완료',
    totalAmount: 350000,
    paidAmount: 350000,
    isSubscription: true,
  },
  {
    id: 'ORD-018',
    tags: ['특수작업'],
    customer: { name: '이이서', email: 'eeseo.lee@starship-ent.com' },
    registeredAt: '2024/02/27',
    title: 'IVE (아이브) – Royal 클래식 현악 사중주 버전',
    status: '등록됨',
    paymentStatus: '등록됨',
    totalAmount: 580000,
    paidAmount: 290000,
    isSubscription: false,
  },
  {
    id: 'ORD-019',
    tags: ['블랙', '원스텝'],
    customer: { name: '레이', email: 'rei@starship-ent.com' },
    registeredAt: '2024/02/26',
    title: 'IVE (아이브) – Kitsch 펑크 록 편곡 버전',
    status: '완료',
    paymentStatus: '완료',
    totalAmount: 430000,
    paidAmount: 430000,
    isSubscription: false,
  },
  {
    id: 'ORD-020',
    tags: ['원스텝'],
    customer: { name: '리즈', email: 'liz@starship-ent.com' },
    registeredAt: '2024/02/25',
    title: 'IVE (아이브) – I AM 피아노 발라드 버전',
    status: '진행중',
    paymentStatus: '진행중',
    totalAmount: 330000,
    paidAmount: 165000,
    isSubscription: true,
  },
  {
    id: 'ORD-021',
    tags: ['특수작업'],
    customer: { name: '강해린', email: 'haerin.kang@ador.kr' },
    registeredAt: '2024/02/24',
    title: 'NewJeans (뉴진스) – Attention 슬로우 재즈 버전',
    status: '반려',
    paymentStatus: '취소',
    totalAmount: 290000,
    paidAmount: 0,
    isSubscription: false,
  },
  {
    id: 'ORD-022',
    tags: ['블랙'],
    customer: { name: '김민지', email: 'minji.kim@ador.kr' },
    registeredAt: '2024/02/23',
    title: 'NewJeans (뉴진스) – Hype Boy 레트로 디스코 버전',
    status: '진행중',
    paymentStatus: '완료',
    totalAmount: 340000,
    paidAmount: 340000,
    isSubscription: false,
  },
  {
    id: 'ORD-023',
    tags: ['원스텝', '특수작업'],
    customer: { name: '팜하니', email: 'hanni.pham@ador.kr' },
    registeredAt: '2024/02/22',
    title: 'NewJeans (뉴진스) – Cookie 어반 R&B 편곡',
    status: '완료',
    paymentStatus: '완료',
    totalAmount: 410000,
    paidAmount: 410000,
    isSubscription: true,
  },
  {
    id: 'ORD-024',
    tags: ['블랙'],
    customer: { name: '모다니엘', email: 'danielle.mo@ador.kr' },
    registeredAt: '2024/02/21',
    title: 'NewJeans (뉴진스) – Ditto 어쿠스틱 포크 버전',
    status: '등록됨',
    paymentStatus: '등록됨',
    totalAmount: 260000,
    paidAmount: 130000,
    isSubscription: false,
  },
  {
    id: 'ORD-025',
    tags: ['원스텝'],
    customer: { name: '이혜인', email: 'hyein.lee@ador.kr' },
    registeredAt: '2024/02/20',
    title: 'NewJeans (뉴진스) – OMG 일렉트로닉 댄스 리믹스',
    status: '진행중',
    paymentStatus: '완료',
    totalAmount: 380000,
    paidAmount: 380000,
    isSubscription: false,
  },
  {
    id: 'ORD-026',
    tags: ['특수작업'],
    customer: { name: '최수빈', email: 'soobin.choi@txt.com' },
    registeredAt: '2024/02/19',
    title: 'TXT (투모로우바이투게더) – Crown 오케스트라 팝 버전',
    status: '완료',
    paymentStatus: '완료',
    totalAmount: 490000,
    paidAmount: 490000,
    isSubscription: true,
  },
  {
    id: 'ORD-027',
    tags: ['블랙', '원스텝'],
    customer: { name: '연준', email: 'yeonjun@txt.com' },
    registeredAt: '2024/02/18',
    title: 'TXT (투모로우바이투게더) – Blue Hour 시티팝 편곡',
    status: '진행중',
    paymentStatus: '진행중',
    totalAmount: 420000,
    paidAmount: 210000,
    isSubscription: false,
  },
  {
    id: 'ORD-028',
    tags: ['특수작업'],
    customer: { name: '범규', email: 'beomgyu@txt.com' },
    registeredAt: '2024/02/17',
    title: 'TXT (투모로우바이투게더) – 0X1=LOVESONG 록 발라드 버전',
    status: '등록됨',
    paymentStatus: '등록됨',
    totalAmount: 360000,
    paidAmount: 180000,
    isSubscription: false,
  },
  {
    id: 'ORD-029',
    tags: ['원스텝'],
    customer: { name: '태현', email: 'taehyun@txt.com' },
    registeredAt: '2024/02/16',
    title: 'TXT (투모로우바이투게더) – Magic Island 어쿠스틱 버전',
    status: '완료',
    paymentStatus: '완료',
    totalAmount: 300000,
    paidAmount: 300000,
    isSubscription: true,
  },
  {
    id: 'ORD-030',
    tags: ['블랙', '특수작업'],
    customer: { name: '휴닝카이', email: 'hueningkai@txt.com' },
    registeredAt: '2024/02/15',
    title: 'TXT (투모로우바이투게더) – Good Boy Gone Bad EDM 리믹스',
    status: '진행중',
    paymentStatus: '완료',
    totalAmount: 520000,
    paidAmount: 520000,
    isSubscription: false,
  },
];

// jobs를 포함한 완전한 sampleOrders 생성
const sampleOrders: Order[] = baseSampleOrders.map((order) => {
  // 각 주문에 따라 다른 jobs 생성
  let jobs: JamakeJob[] = [];
  
  if (order.id === 'ORD-002' || order.id === '01JGHPS9T2JQKFRN8CVTXSYQ6Z' || order.id === '01JGHPSGDTWRKFRQ4JVTXSYQ3F' || order.id === '01JGHPSLM1BVKFRN4NVTXSYQ7K') {
    // 50+ jobs (숏폼) for these orders
    for (let i = 1; i <= 52; i++) {
      jobs.push(createJob(
        `[숏폼] ${order.title} - 파트 ${i}`,
        '한국어',
        i % 4 === 0 ? '영어' : (i % 4 === 1 ? '일본어' : (i % 4 === 2 ? '중국어' : '스페인어')),
        i % 3 === 0 ? '번역' : '캡션',
        i % 4 === 0 ? '화면+음성' : (i % 2 === 0 ? '화면' : '음성'),
        `00:0${Math.floor(Math.random() * 5) + 1}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`,
        order.registeredAt,
        i % 5 === 0 ? '정확한 번역 필요' : undefined,
        i % 7 === 0 ? 'K-POP 전문 용어집 적용' : undefined
      ));
    }
  } else if (order.id === 'ORD-003') {
    // ~10 jobs for this order
    for (let i = 1; i <= 10; i++) {
      jobs.push(createJob(
        `${order.title} - 섹션 ${i}`,
        '한국어',
        i % 3 === 0 ? '영어' : (i % 3 === 1 ? '일본어' : '중국어'),
        i % 2 === 0 ? '번역' : '캡션',
        i % 3 === 0 ? '화면+음성' : '화면',
        `00:0${Math.floor(Math.random() * 3) + 2}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`,
        order.registeredAt,
        i % 3 === 0 ? '감정 표현 중시' : undefined,
        i % 4 === 0 ? '아이돌 전문 용어집' : undefined
      ));
    }
  } else {
    // 1 job for other orders
    jobs.push(createJob(
      order.title,
      '한국어',
      '영어',
      '번역',
      '화면',
      `00:0${Math.floor(Math.random() * 4) + 2}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`,
      order.registeredAt,
      '고품질 번역 요구',
      'K-POP 전문 용어집 적용'
    ));
  }
  
  return {
    ...order,
    jobs
  };
});

const defaultFilters: OrderFilters = {
  search: '',
  dateRange: {
    start: '',
    end: '',
  },
  statuses: [],
  tags: [],
};

export const useOrderStore = create<OrderStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        orders: sampleOrders,
        filters: defaultFilters,
        currentPage: 1,
        pageSize: 10,
        totalCount: sampleOrders.length,
        filteredOrders: sampleOrders,
        
        // Computed properties
        get totalPages() {
          const { filteredOrders, pageSize } = get();
          return Math.ceil(filteredOrders.length / pageSize);
        },
        
        // Actions
        setFilters: (newFilters) => {
          set((state) => {
            const updatedFilters = { ...state.filters, ...newFilters };
            const filteredOrders = filterOrders(state.orders, updatedFilters);
            return {
              filters: updatedFilters,
              filteredOrders,
              totalCount: filteredOrders.length,
              currentPage: 1, // Reset to first page when filters change
            };
          });
        },
        
        setCurrentPage: (page) => set({ currentPage: page }),
        
        setPageSize: (size) => set(() => ({
          pageSize: size,
          currentPage: 1, // Reset to first page when page size changes
        })),
        
        resetFilters: () => {
          set((state) => ({
            filters: defaultFilters,
            filteredOrders: state.orders,
            totalCount: state.orders.length,
            currentPage: 1,
          }));
        },
        
        updateFilteredOrders: () => {
          set((state) => {
            const filteredOrders = filterOrders(state.orders, state.filters);
            return {
              filteredOrders,
              totalCount: filteredOrders.length,
            };
          });
        },
        
        getOrdersByIds: (orderIds: string[]) => {
          const { orders } = get();
          return orders.filter(order => orderIds.includes(order.id));
        },
      }),
      {
        name: 'order-store',
        partialize: (state) => ({
          filters: state.filters,
          currentPage: state.currentPage,
          pageSize: state.pageSize,
        }),
      }
    ),
    {
      name: 'order-store',
    }
  )
);

// Helper function to filter orders
function filterOrders(orders: Order[], filters: OrderFilters): Order[] {
  return orders.filter((order) => {
    // Text search (customer name, email, title)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesSearch = 
        order.customer.name.toLowerCase().includes(searchLower) ||
        order.customer.email.toLowerCase().includes(searchLower) ||
        order.title.toLowerCase().includes(searchLower) ||
        order.registeredAt.includes(searchLower);
      
      if (!matchesSearch) return false;
    }
    
    // Date range filter - convert both to YYYY-MM-DD format for comparison
    if (filters.dateRange.start) {
      const orderDateFormatted = order.registeredAt.replace(/\//g, '-'); // Convert YYYY/MM/DD to YYYY-MM-DD
      const startDateFormatted = filters.dateRange.start; // HTML date input is already YYYY-MM-DD
      if (orderDateFormatted < startDateFormatted) {
        return false;
      }
    }
    if (filters.dateRange.end) {
      const orderDateFormatted = order.registeredAt.replace(/\//g, '-'); // Convert YYYY/MM/DD to YYYY-MM-DD
      const endDateFormatted = filters.dateRange.end; // HTML date input is already YYYY-MM-DD
      if (orderDateFormatted > endDateFormatted) {
        return false;
      }
    }
    
    // Status filter
    if (filters.statuses.length > 0 && !filters.statuses.includes(order.status)) {
      return false;
    }
    
    // Tags filter
    if (filters.tags.length > 0) {
      const hasMatchingTag = filters.tags.some(tag => order.tags.includes(tag));
      if (!hasMatchingTag) return false;
    }
    
    return true;
  });
}
